"use client"
import { useMemo, useRef, useState } from "react"
import { useReview, reviewActions } from "./review-store"
import { bucketOf, type Bucket } from "@/lib/review/bucket"
import type { QueueItem } from "@/lib/review/queue"
import { dispatchCart } from "@/components/editor/edit-cart"
import { getReviewPr } from "./review-pr"
import { isLargeString } from "./locate-state"
import { LargeStringEditor } from "./large-string-editor"

type Filter = "all" | Bucket

const FILTERS: { f: Filter; label: string }[] = [
  { f: "all", label: "All" },
  { f: "safe", label: "Identical · safe" },
  { f: "trap", label: "⚠ Needs attention" },
  { f: "differs", label: "Differs from EN" },
]

const STATUS: Record<Bucket, string> = {
  safe: "identical",
  trap: "⚠",
  differs: "localized",
}

async function postBatch(
  items: Array<{
    key: string
    locale: string
    enValue: string
    approvedValue: string
    pendingMerge: boolean
  }>,
  idToken: string
) {
  const res = await fetch("/api/review/signoff/batch", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "User-Authorization": `Bearer ${idToken}`,
    },
    body: JSON.stringify({ items, pr: getReviewPr() ?? undefined }),
  })
  if (!res.ok) throw new Error("batch failed")
  return (await res.json()) as {
    approved: string[]
    skipped: Array<{ key: string; reason: string }>
    recheckRequested?: boolean
  }
}

export function BatchTable({
  idToken,
  locale,
  onOpenFocus,
}: {
  idToken: string
  locale: string
  onOpenFocus: (key: string) => void
}) {
  const { queue, selected } = useReview()
  const [filter, setFilter] = useState<Filter>("all")
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [toast, setToast] = useState<string | null>(null)
  const [modalKey, setModalKey] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [approveAllBusy, setApproveAllBusy] = useState(false)
  const anchorRef = useRef<number | null>(null)

  const counts = useMemo(() => {
    const c = { safe: 0, trap: 0, differs: 0 }
    queue.forEach((i) => (c[bucketOf(i)] += 1))
    return c
  }, [queue])

  const rows = useMemo(
    () =>
      filter === "all" ? queue : queue.filter((i) => bucketOf(i) === filter),
    [queue, filter]
  )

  const valueOf = (i: QueueItem) => edits[i.key] ?? i.targetValue
  const isDirty = (i: QueueItem) => valueOf(i).trim() !== i.targetValue.trim()

  const visKeys = rows.map((i) => i.key)
  const onVis = visKeys.filter((k) => selected.has(k))
  const allChecked = visKeys.length > 0 && onVis.length === visKeys.length

  function flash(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2800)
  }

  function edit(i: QueueItem, value: string) {
    setEdits((e) => ({ ...e, [i.key]: value }))
  }

  // Row checkbox click: plain toggle, or (shift) select the range from the last
  // anchor to this row within the current filter.
  function clickRow(e: React.MouseEvent, idx: number) {
    const key = rows[idx].key
    if (e.shiftKey && anchorRef.current !== null) {
      const [a, b] = [
        Math.min(anchorRef.current, idx),
        Math.max(anchorRef.current, idx),
      ]
      reviewActions.selectKeys(
        rows.slice(a, b + 1).map((r) => r.key),
        !selected.has(key)
      )
    } else {
      reviewActions.toggleSelected(key)
    }
    anchorRef.current = idx
  }

  async function approve(keys: string[], fromApproveAll = false) {
    if (busy || keys.length === 0) return
    setBusy(true)
    if (fromApproveAll) setApproveAllBusy(true)
    const picked = queue.filter((i) => keys.includes(i.key))
    const items = picked.map((i) => {
      const dirty = isDirty(i)
      if (dirty) {
        dispatchCart({
          type: "add",
          item: {
            key: i.key,
            source: "intl",
            page: i.jumpPath || "/",
            values: { [locale]: { old: i.targetValue, new: valueOf(i) } },
          },
        })
      }
      return {
        key: i.key,
        locale,
        enValue: i.enValue,
        approvedValue: valueOf(i),
        pendingMerge: dirty,
      }
    })
    try {
      const { approved, skipped } = await postBatch(items, idToken)
      reviewActions.completeKeys(approved)
      const edited = picked.filter(
        (i) => approved.includes(i.key) && isDirty(i)
      ).length
      flash(
        `✓ Approved ${approved.length}` +
          (edited ? ` (${edited} edited → edit & approve)` : "") +
          (skipped.length
            ? ` · ${skipped.length} skipped (${skipped[0].reason})`
            : "")
      )
    } catch {
      flash("⚠ Couldn't reach the review database — selection kept, try again.")
    } finally {
      setBusy(false)
      setApproveAllBusy(false)
    }
  }

  const safeKeys = queue.filter((i) => bucketOf(i) === "safe").map((i) => i.key)
  const modalItem = modalKey
    ? (queue.find((i) => i.key === modalKey) ?? null)
    : null

  return (
    <div className="ahead-review-table-panel" data-ahead-editor-ui>
      <div className="ahead-review-table-triage">
        <div className="row">
          <span className="dot safe" />
          <span>
            <b>{counts.safe}</b> identical to English, no market terms
          </span>
          {safeKeys.length > 0 ? (
            <button
              className="approve-all"
              disabled={busy}
              aria-busy={approveAllBusy}
              onClick={() => approve(safeKeys, true)}
            >
              {approveAllBusy ? (
                <span className="ahead-review-spinner" aria-hidden="true" />
              ) : null}
              Approve all {counts.safe} →
            </button>
          ) : null}
          <span className="hint">
            one click signs off only these · nothing is auto-approved
          </span>
        </div>
        <div className="row sub">
          <span>The rest stay in the table for you to review:</span>
          <button className="jump warn" onClick={() => setFilter("trap")}>
            ⚠ {counts.trap} contain market terms — may need localizing
          </button>
          <button className="jump" onClick={() => setFilter("differs")}>
            {counts.differs} already differ from English
          </button>
        </div>
      </div>

      <div className="ahead-review-table-filters">
        {FILTERS.map(({ f, label }) => (
          <button
            key={f}
            className={f === filter ? "chip on" : "chip"}
            onClick={() => setFilter(f)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="ahead-review-table-scroll">
        <table className="ahead-review-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = onVis.length > 0 && !allChecked
                  }}
                  onChange={(e) =>
                    reviewActions.selectKeys(visKeys, e.target.checked)
                  }
                />
              </th>
              <th>Key</th>
              <th>Tier</th>
              <th>EN (reference)</th>
              <th>{locale}</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((i, idx) => {
              const b = bucketOf(i)
              const dirty = isDirty(i)
              return (
                <tr
                  key={i.key}
                  className={
                    selected.has(i.key) ? "sel" : b === "trap" ? "trap" : ""
                  }
                >
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${i.key}`}
                      checked={selected.has(i.key)}
                      onChange={() => {}}
                      onClick={(e) => clickRow(e, idx)}
                    />
                  </td>
                  <td className="key">{i.key}</td>
                  <td>
                    <span className={`tier ${i.tier}`}>{i.tier}</span>
                  </td>
                  <td className="en">
                    <div className="en-clip">{i.enValue}</div>
                  </td>
                  <td>
                    {isLargeString(i.enValue) ? (
                      <div className="nl-large">
                        <p className="nl-large-preview">
                          {valueOf(i).slice(0, 160)}
                          {valueOf(i).length > 160 ? "…" : ""}
                        </p>
                        <button
                          type="button"
                          className="nl-large-open"
                          onClick={() => setModalKey(i.key)}
                        >
                          {dirty
                            ? "Edited · open editor"
                            : "Edit in full editor"}{" "}
                          ↗
                        </button>
                      </div>
                    ) : (
                      <textarea
                        className={dirty ? "nl dirty" : "nl"}
                        aria-label={`${locale} ${i.key}`}
                        rows={1}
                        value={valueOf(i)}
                        onChange={(e) => edit(i, e.target.value)}
                      />
                    )}
                  </td>
                  <td className="status">
                    {dirty ? (
                      <span className="badge edited">edited</span>
                    ) : (
                      <span className={`badge ${b}`}>
                        {STATUS[b]}
                        {b === "trap"
                          ? i.marketTokens.length
                            ? ` ${i.marketTokens[0]}`
                            : " untranslated"
                          : ""}
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      className="open"
                      aria-label={`Open ${i.key} in focus`}
                      onClick={() => onOpenFocus(i.key)}
                    >
                      ↗
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {selected.size > 0 ? (
        <div className="ahead-review-pill" role="status">
          <span className="n">{selected.size} selected</span>
          <button
            className="ap"
            disabled={busy}
            onClick={() => approve(Array.from(selected))}
          >
            Approve
          </button>
          <button
            className="gh"
            onClick={() => onOpenFocus(Array.from(selected)[0])}
          >
            ↗ Open first in focus
          </button>
          <button className="clr" onClick={() => reviewActions.clearSelected()}>
            Clear
          </button>
        </div>
      ) : null}

      {toast ? <div className="ahead-review-pill-toast">{toast}</div> : null}

      {modalItem ? (
        <LargeStringEditor
          keyName={modalItem.key}
          tier={modalItem.tier}
          enValue={modalItem.enValue}
          value={valueOf(modalItem)}
          dirty={isDirty(modalItem)}
          busy={busy}
          blocked={false}
          onChange={(v) => edit(modalItem, v)}
          onApprove={() => {
            approve([modalItem.key])
            setModalKey(null)
          }}
          onEditApprove={() => {
            approve([modalItem.key])
            setModalKey(null)
          }}
          onClose={() => setModalKey(null)}
        />
      ) : null}
    </div>
  )
}
